import { chartLayoutColours } from '@risksmart-app/components/src/utils/colours';
import type { PointClickCallbackFunction } from 'highcharts';
import { useMemo } from 'react';

export interface BubbleSizeRange {
  /** Pixel diameter of the smallest bubble. */
  minSize: number;
  /** Pixel diameter of the largest bubble. */
  maxSize: number;
}

/**
 * Every bubble renders at this diameter until something is encoded in size.
 * Equal bounds make Highcharts ignore `z`.
 */
export const FIXED_BUBBLE_SIZE: BubbleSizeRange = { minSize: 14, maxSize: 14 };

/**
 * The range a size encoding maps into (RSP-7292). The floor is the fixed
 * size, so the smallest measured bubble is exactly the dot the chart draws
 * when size encodes nothing, and never smaller. The ceiling is pixels rather
 * than a share of the plot so the area ratio between the smallest and largest
 * bubble (about 8x) reads the same in every widget size.
 */
export const ENCODED_BUBBLE_SIZE: BubbleSizeRange = {
  minSize: 14,
  maxSize: 40,
};

/**
 * Bubbles are drawn translucent so that where they overlap, the one beneath
 * is still visible (RSP-7294). The cost is that an overlap reads as a colour
 * that is neither bubble's own; a bubble's rating is carried by its shape and
 * stated in its tooltip as well, so the blend never has to be decoded.
 */
export const BUBBLE_FILL_OPACITY = 0.75;

interface BubbleChartOptions {
  subtitle?: string;
  onPointClick?: PointClickCallbackFunction;
  bubbleSize?: BubbleSizeRange;
}

export const useGetBubbleChartDefaultOptions = ({
  subtitle,
  onPointClick,
  bubbleSize = FIXED_BUBBLE_SIZE,
}: BubbleChartOptions): Highcharts.Options => {
  return useMemo(() => {
    const options: Highcharts.Options = {
      chart: {
        type: 'bubble',
      },
      subtitle: {
        text: subtitle || '',
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        series: {
          // Background regions are their own series behind the bubbles, so
          // hovering a bubble must not fade them.
          states: {
            inactive: {
              opacity: 1,
            },
          },
        },
        bubble: {
          minSize: bubbleSize.minSize,
          maxSize: bubbleSize.maxSize,
          // Readers judge a bubble by its area, so a value twice another's
          // gets twice the area, not twice the diameter.
          sizeBy: 'area',
          // Translucent enough that a bubble underneath another still shows
          // through, opaque enough (with the light edge) to stand out on a
          // washed-out band of its own colour. Highcharts' default fill is
          // paler and would vanish there.
          marker: {
            fillOpacity: BUBBLE_FILL_OPACITY,
            lineWidth: 1,
            lineColor: chartLayoutColours.seriesBorders,
          },
          // The lowest declared score is a common position and lies on the
          // axis, so let a bubble there draw whole rather than be halved.
          clip: false,
          cursor: onPointClick ? 'pointer' : undefined,
          point: {
            events: {
              click: onPointClick,
            },
          },
        },
      },
      tooltip: {
        headerFormat: '',
        pointFormat: '<b>{point.name}</b>',
      },
    };

    return options;
  }, [subtitle, onPointClick, bubbleSize]);
};
